import { useCallback, useState, useEffect, useRef } from "react";
import { Button } from "@/components/Button/Button";
import { Input } from "@/components/form/Input";
import { InputLabel } from "@/components/form/InputLabel";
import { useAppDispatch, useAppSelector } from "@/hooks/types";
import {
    PreferencesStep,
    PreferenceTitle,
} from "@/pages/Preferences/components";
import { JobRoleList } from "@/pages/Preferences/components/JobRoleList";
import { jobRoles } from "@/pages/Preferences/constants";
import { getUserPreferenceJobRoles } from "@/store/wizard/selectors";
import { setWizardUserPreference } from "@/store/wizard/slice";
import { PreferenceOptions } from "@/types";

export const PreferencesJobRole = () => {
    const dispatch = useAppDispatch();
    const selectedJobRoles = useAppSelector(getUserPreferenceJobRoles);
    const [otherChecked, setOtherChecked] = useState(false);
    const [otherJobRole, setOtherJobRole] = useState("");
    const [additionalJobRoles, setAdditionalJobRoles] =
        useState<PreferenceOptions>([]);
    const [roles, setRoles] = useState<PreferenceOptions>([]);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const uniqueRoles = new Map();
        jobRoles.forEach(role => uniqueRoles.set(role.value, role));
        selectedJobRoles.forEach(role => {
            if (!uniqueRoles.has(role)) {
                uniqueRoles.set(role, { label: role, value: role });
            }
        });
        setRoles(Array.from(uniqueRoles.values()));
    }, [selectedJobRoles]);

    const onJobRoleClick = useCallback(
        (jobRole: string, selected: boolean) => {
            let newJobRoles = [...selectedJobRoles];
            if (selected) {
                newJobRoles.push(jobRole);
            } else {
                newJobRoles = newJobRoles.filter(
                    selectedJobRole => selectedJobRole !== jobRole,
                );
                setAdditionalJobRoles(
                    additionalJobRoles.filter(role => role.value !== jobRole),
                );
            }

            dispatch(
                setWizardUserPreference({
                    preference: "jobRoles",
                    value: newJobRoles,
                }),
            );
        },
        [additionalJobRoles, dispatch, selectedJobRoles],
    );

    const handleOtherChange = useCallback(
        (checked: boolean) => {
            setOtherChecked(checked);
            if (!checked) {
                setOtherJobRole("");
            }
        },
        [setOtherChecked, setOtherJobRole],
    );

    const handleOtherJobRoleChange = useCallback(
        (value: string) => {
            setOtherJobRole(value);
        },
        [setOtherJobRole],
    );

    const persistOtherJobRole = useCallback(() => {
        if (otherJobRole) {
            if (!additionalJobRoles.some(role => role.value === otherJobRole)) {
                setAdditionalJobRoles([
                    ...additionalJobRoles,
                    { label: otherJobRole, value: otherJobRole },
                ]);
                dispatch(
                    setWizardUserPreference({
                        preference: "jobRoles",
                        value: [...selectedJobRoles, otherJobRole],
                    }),
                );
                setOtherJobRole("");

                setTimeout(() => {
                    if (scrollContainerRef.current) {
                        scrollContainerRef.current.scrollTop =
                            scrollContainerRef.current.scrollHeight;
                    }
                }, 0);
            }
        }
    }, [additionalJobRoles, dispatch, otherJobRole, selectedJobRoles]);

    return (
        <PreferencesStep>
            <PreferenceTitle title="What type of role(s) are you currently working in or most interested in pursuing?" />
            <div
                ref={scrollContainerRef}
                className="h-[200px] overflow-y-scroll bg-gradient-to-b from-transparent from-95% to-black/10 px-2 py-2 pb-4 lg:h-[250px]"
            >
                <div className="grid gap-x-6 gap-y-4 md:grid-cols-3 xl:grid-cols-3">
                    <JobRoleList
                        onJobRoleClick={onJobRoleClick}
                        selectedJobRoles={selectedJobRoles}
                        showOther={!otherChecked}
                        otherChecked={otherChecked}
                        onOtherClick={handleOtherChange}
                        roles={roles}
                    />
                </div>
            </div>
            {otherChecked && (
                <div className="mt-4 w-64">
                    <InputLabel label="Enter your job role type" />
                    <div className="mt-2 flex items-center gap-2">
                        <Input
                            type="text"
                            value={otherJobRole}
                            onChange={(_, value) =>
                                handleOtherJobRoleChange(value)
                            }
                            maxLength={30}
                            onKeyDown={e => {
                                if (e.key === "Enter") {
                                    persistOtherJobRole();
                                }
                            }}
                        />
                        <Button
                            onClick={persistOtherJobRole}
                            disabled={
                                !otherJobRole ||
                                additionalJobRoles.some(
                                    role => role.value === otherJobRole,
                                )
                            }
                        >
                            Add
                        </Button>
                    </div>
                </div>
            )}
        </PreferencesStep>
    );
};
